import { S3Client, ListObjectsV2Command } from '@aws-sdk/client-s3';
import { Upload } from '@aws-sdk/lib-storage';
import { v4 as uuidv4 } from 'uuid';
import credentials from './config';
import { GeneratePresignedUrlRequest, GeneratePresignedUrlRequestActionEnum, DefaultApi } from '../../client-sdk';

const {
  s3: { region, bucketName, accessKeyId, secretAccessKey, dirName },
} = credentials;

// Base URL for S3 objects
const getS3BaseUrl = () => {
  return `https://${bucketName}.s3.${region}.amazonaws.com`;
};
// Create S3 client
const s3Client = new S3Client({
  region,
  credentials: {
    accessKeyId,
    secretAccessKey,
  },
});

export const uploadImage = async (file: File) => {
  try {
    const uniqueFileName = `${dirName}/${uuidv4()}-${file.name}`;

    const upload = new Upload({
      client: s3Client,
      params: {
        Bucket: bucketName,
        Key: uniqueFileName,
        Body: file,
        ContentType: file.type,
      },
    });

    await upload.done();

    const baseUrl = getS3BaseUrl();
    const url = `${baseUrl}/${uniqueFileName}`;

    return {
      key: uniqueFileName,
      url,
    };
  } catch (error) {
    console.error('Upload Error:', error);
    throw error;
  }
};

export const fetchAllImages = async (): Promise<string[]> => {
  try {
    const command = new ListObjectsV2Command({
      Bucket: bucketName,
      Prefix: `${dirName}/`,
    });

    const response = await s3Client.send(command);

    const baseUrl = getS3BaseUrl();
    const urls =
      response.Contents?.map((item) => `${baseUrl}/${item.Key}`) || [];

    return urls;
  } catch (error) {
    console.error('Fetch Error:', error);
    throw error;
  }
};

// New function for presigned URL upload
export const uploadImageWithPresignedUrl = async (file: File, apiClient: DefaultApi): Promise<{ key: string; url: string }> => {
  try {
    // Generate unique filename with proper extension
    const fileExtension = file.name.split('.').pop() || 'jpg';
    const uniqueFileName = `${dirName}/${uuidv4()}.${fileExtension}`;
    
    // Request presigned URL
    const presignedUrlRequest: GeneratePresignedUrlRequest = {
      action: GeneratePresignedUrlRequestActionEnum.Upload,
      fileName: uniqueFileName
    };
    
    const presignedUrlResponse = await apiClient.generatePresignedUrl(presignedUrlRequest);
    const presignedUrlData = presignedUrlResponse.data;
    
    // Upload file to S3 using presigned URL
    const uploadResponse = await fetch(presignedUrlData.signedUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type,
      },
    });
    
    if (!uploadResponse.ok) {
      throw new Error(`Upload failed with status: ${uploadResponse.status}`);
    }
    
    // Extract the base URL from the presigned URL response
    // The presigned URL contains the full S3 URL, we need to extract the base part
    const signedUrl = new URL(presignedUrlData.signedUrl);
    const baseUrl = `${signedUrl.protocol}//${signedUrl.host}`;
    // The uniqueFileName already includes dirName (media), so we need to add another media segment
    // Final URL structure: https://bucket.s3.region.amazonaws.com/media/media/uuid-123.jpg
    const finalUrl = `${baseUrl}/media/${uniqueFileName}`;
    
    return {
      key: uniqueFileName,
      url: finalUrl,
    };
  } catch (error) {
    console.error('Presigned URL Upload Error:', error);
    throw error;
  }
};

// Function to get presigned URL for viewing/downloading images
export const getPresignedUrlForImage = async (fileName: string, apiClient: DefaultApi): Promise<string> => {
  try {
    const presignedUrlRequest: GeneratePresignedUrlRequest = {
      action: GeneratePresignedUrlRequestActionEnum.Download,
      fileName: fileName
    };
    
    const presignedUrlResponse = await apiClient.generatePresignedUrl(presignedUrlRequest);
    const presignedUrlData = presignedUrlResponse.data;
    
    return presignedUrlData.signedUrl;
  } catch (error) {
    console.error('Presigned URL Download Error:', error);
    throw error;
  }
};